import { Build, Component, Element, forceUpdate, h, Host, Method, Prop, readTask, State, Watch } from '@stencil/core';
import { isBotRequest } from '~service/bot-service';
import { translate } from '~service/translation-service';

@Component({
  tag: 'staedtler-cookie-banner',
  styleUrl: 'cookie-banner.scss',
})
export class CookieBanner {
  @Element() el!: HTMLStaedtlerCookieBannerElement;

  @Prop() forceClosed = false;

  @State() visible = false;
  @State() userInteraction = false;

  @State() consentSettings: ConsentSettings = {
    essential: {
      measurement: true,
      friendlyCaptcha: true,
    },
    statistic: {
      googleAnalytics: false,
      googleDoubleclick: false,
      googleAds: false,
      facebook: false,
    },
    functional: {
      youtube: false,
      googleMaps: false,
    },
  };

  private consentManager: StaedtlerConsentManager;

  @Watch('visible')
  visibleWatcher() {
    if (this.visible) {
      this.el.removeAttribute('hidden');
    } else {
      this.el.setAttribute('hidden', 'hidden');
    }
  }

  @Method()
  async open() {
    this.visible = true;
  }

  @Method()
  async close() {
    this.visible = false;
  }

  constructor() {
    this.consentManager = window['staedtlerConsentManager'];
  }

  componentWillLoad() {
    if (isBotRequest() || !Build.isBrowser) {
      return;
    }
    this.consentManager.addListener(this.settingsChangedListener);
    this.consentSettings = Object.assign(this.consentSettings, this.consentManager.consentSettings);
    this.visible = !this.consentManager.hasCookie;
    if (this.forceClosed) {
      this.visible = false;
    }
    if (this.visible) {
      this.trackEvent('view');
    }
    this.visibleWatcher();
  }

  componentDidLoad() {
    readTask(() => this.visibleWatcher());
  }

  disconnectedCallback() {
    this.consentManager.removeListener(this.settingsChangedListener);
  }

  private settingsChangedListener = (consentSettings: ConsentSettings) => {
    this.consentSettings = Object.assign(this.consentSettings, consentSettings);
    forceUpdate(this.el);
  };

  private save(event: MouseEvent) {
    event.preventDefault();
    this.consentManager.updateConsentSettings(this.consentSettings);
    this.trackEvent(this.userInteraction ? 'accept' : 'reject', this.consentManager.consentSettings);
    this.close();
  }

  private saveAll(event: MouseEvent) {
    event.preventDefault();
    this.consentManager.updateConsentSettings({
      essential: {
        measurement: true,
        friendlyCaptcha: true,
      },
      statistic: {
        googleAnalytics: true,
        googleDoubleclick: true,
        googleAds: true,
        facebook: true,
      },
      functional: {
        youtube: true,
        googleMaps: true,
      },
    });
    this.trackEvent('accept_all', this.consentManager.consentSettings);
    this.close();
  }

  private applyChanges(groupName: string, name: string, active: boolean) {
    this.userInteraction = true;
    const group = this.consentSettings[groupName];
    group[name] = active;
    this.consentSettings = Object.assign({}, this.consentSettings, { [groupName]: group });
  }

  private toggleGroup(groupName: string) {
    this.userInteraction = true;
    const active = this.groupActive(groupName);
    const group = this.consentSettings[groupName];
    Object.keys(group).forEach(key => (group[key] = !active));
    this.consentSettings = Object.assign({}, this.consentSettings, { [groupName]: group });
  }

  private groupActive(groupName: string) {
    return Object.values(this.consentSettings[groupName]).some(value => value === true);
  }

  render() {
    return (
      <Host>
        <div class="modal modal-cookie">
          <div class="modal-dialog">
            <div class="modal-content">
              <h2 innerHTML={translate('cookiebanner.headline')} />
              <p innerHTML={translate('cookiebanner.text')} />

              <div class="accordion">
                <div class="accordion-item">
                  <div class="accordion-header">
                    <button class="accordion-toggle" onClick={e => (e.target as HTMLElement).closest('.accordion-item').classList.toggle('open')} />
                    <div class="form-check form-switch form-check-reverse">
                      <input class="form-check-input" type="checkbox" role="switch" id="cb-essential" disabled checked />
                      <label class="form-check-label" htmlFor="cb-essential" onClick={e => e.preventDefault()}>
                        {translate('cookiebanner.essential')}
                      </label>
                    </div>
                  </div>
                  <div class="accordion-collapse">
                    <div class="form-check form-switch form-check-reverse">
                      <input class="form-check-input" type="checkbox" role="switch" id="cb-measurement" disabled checked />
                      <label class="form-check-label" htmlFor="cb-measurement">
                        {translate('cookiebanner.essential.measurement')}
                      </label>
                    </div>
                    <div class="form-check form-switch form-check-reverse">
                      <input class="form-check-input" type="checkbox" role="switch" id="cb-friendlyCaptcha" disabled checked />
                      <label class="form-check-label" htmlFor="cb-friendlyCaptcha">
                        Friendly Captcha
                      </label>
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <div class="accordion-header">
                    <button class="accordion-toggle" onClick={e => (e.target as HTMLElement).closest('.accordion-item').classList.toggle('open')} />
                    <div class="form-check form-switch form-check-reverse">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="cb-statistic"
                        checked={this.groupActive('statistic')}
                        onChange={_ => this.toggleGroup('statistic')}
                      />
                      <label class="form-check-label" htmlFor="cb-statistic">
                        {translate('cookiebanner.statistic')}
                      </label>
                    </div>
                  </div>
                  <div class="accordion-collapse">
                    <div class="form-check form-switch form-check-reverse">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="cb-google-analytics"
                        checked={this.consentSettings.statistic.googleAnalytics}
                        onChange={e => this.applyChanges('statistic', 'googleAnalytics', (e.target as HTMLInputElement).checked)}
                      />
                      <label class="form-check-label" htmlFor="cb-google-analytics">
                        Google Analytics
                      </label>
                    </div>
                    <div class="form-check form-switch form-check-reverse">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="cb-google-doubleclick"
                        checked={this.consentSettings.statistic.googleDoubleclick}
                        onChange={e => this.applyChanges('statistic', 'googleDoubleclick', (e.target as HTMLInputElement).checked)}
                      />
                      <label class="form-check-label" htmlFor="cb-google-doubleclick">
                        Google Doubleclick
                      </label>
                    </div>
                    <div class="form-check form-switch form-check-reverse">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="cb-google-ads"
                        checked={this.consentSettings.statistic.googleAds}
                        onChange={e => this.applyChanges('statistic', 'googleAds', (e.target as HTMLInputElement).checked)}
                      />
                      <label class="form-check-label" htmlFor="cb-google-ads">
                        Google Ads
                      </label>
                    </div>
                    <div class="form-check form-switch form-check-reverse">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="cb-facebook"
                        checked={this.consentSettings.statistic.facebook}
                        onChange={e => this.applyChanges('statistic', 'facebook', (e.target as HTMLInputElement).checked)}
                      />
                      <label class="form-check-label" htmlFor="cb-facebook">
                        Facebook
                      </label>
                    </div>
                  </div>
                </div>
                <div class="accordion-item">
                  <div class="accordion-header">
                    <button class="accordion-toggle" onClick={e => (e.target as HTMLElement).closest('.accordion-item').classList.toggle('open')} />
                    <div class="form-check form-switch form-check-reverse">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="cb-functional"
                        checked={this.groupActive('functional')}
                        onChange={_ => this.toggleGroup('functional')}
                      />
                      <label class="form-check-label" htmlFor="cb-functional">
                        {translate('cookiebanner.functional')}
                      </label>
                    </div>
                  </div>
                  <div class="accordion-collapse">
                    <div class="form-check form-switch form-check-reverse">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="cb-youtube"
                        checked={this.consentSettings.functional.youtube}
                        onChange={e => this.applyChanges('functional', 'youtube', (e.target as HTMLInputElement).checked)}
                      />
                      <label class="form-check-label" htmlFor="cb-youtube">
                        YouTube
                      </label>
                    </div>
                    <div class="form-check form-switch form-check-reverse">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="cb-google-maps"
                        checked={this.consentSettings.functional.googleMaps}
                        onChange={e => this.applyChanges('functional', 'googleMaps', (e.target as HTMLInputElement).checked)}
                      />
                      <label class="form-check-label" htmlFor="cb-google-maps">
                        Google Maps
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="btn-group">
                <a class="btn btn-primary" role="button" onClick={event => this.saveAll(event)} innerHTML={translate('cookiebanner.button.saveall')} />
                <a
                  class="btn btn-secondary"
                  role="button"
                  onClick={event => this.save(event)}
                  innerHTML={translate(this.userInteraction ? 'cookiebanner.button.save' : 'cookiebanner.button.reject')}
                />
              </div>

              <p innerHTML={translate('cookiebanner.footer')} />
            </div>
          </div>
        </div>
        <div class="modal-backdrop" />
      </Host>
    );
  }

  private trackEvent(action: string, eventParams: Gtag.CustomParams = {}) {
    if ('gtag' in window) {
      gtag('event', `cookiebanner_${action}`, eventParams);
    }

    const trackingHost = window['TYPO3']?.proxy?.tracking || '';
    if (trackingHost !== '') {
      const url = new URL(`cb/${action}`, trackingHost);
      const image = document.createElement('img');
      image.src = url.toString();
      image.height = 1;
      image.width = 1;
      image.style.display = 'none';
    }
  }
}
